// Third-party packages
import {
	openobserveLogs,
} from '@openobserve/browser-logs';
import {
	openobserveRum,
} from '@openobserve/browser-rum';

// Internal libraries (@stonal-tech/*)
import {
	buildUrl,
	getEnv,
	getProviderDomain,
	getStack,
} from '@stonal-tech/lib-front-utils-ts';

// Project modules
import {
	logger,
} from '@config/logger';
import {
	Destination,
} from '@local_types/index';

// Relative imports
import {
	OPENOBSERVE_DESTINATION_NAME,
} from '../config/destinations';
import {
	SESSION_REPLAY_SAMPLE_RATE,
	SESSION_SAMPLE_RATE,
} from '../config/openobserve';
import {
	SESSION_KEY_OO_CURRENT_USER,
} from '../config/storageKeys';
import {
	URL_PATTERNS,
} from '../config/urlPatterns';
import {
	Analytics,
} from './analytics';

const urlParams = new URLSearchParams(window.location.search);
const siteFromUrl = urlParams.get('site');

/**
 * `?norecord=true` disables session replay recording (RUM metrics still flow).
 * Historically a CDN script-tag src parameter (Hotjar era), so both the page
 * URL and the tracking script src are honored.
 */
const isReplayDisabled = function () {
	if (urlParams.get('norecord') === 'true') {
		return true;
	}
	const scripts = document.querySelectorAll('script[src*="tracking.js"]');
	return Array.from(scripts)
		.some(script => (script.getAttribute('src') || '').includes('norecord=true'));
};

/*
// https://chatgpt.com/share/6836f020-98f8-800b-9197-7dcd62d89c63
function isLowEnd() {
  const cores   = navigator.hardwareConcurrency ?? 4;  // logical CPUs

  return cores <= 4;
}
*/

const getServiceInfo = async function () {
	let env = getEnv();
	let name = 'unknown';
	let version = 'unknown';
	const currentUrl = new URL(window.location.href);
	// Determine environment based on hostname

	try {
		let versionUrl: string;

		// app.stonal...
		if (currentUrl.hostname.startsWith('app.')) {
			// Extract app name from path (first segment after domain)
			const pathSegments = currentUrl.pathname.split('/').filter(segment => segment);
			const appName = pathSegments[0];

			// Construct version URL: https://app.$domain/$app/version.json
			versionUrl = `${currentUrl.origin}/${appName}/version.json`;
		}
		// cdn.stonal...
		else if (currentUrl.hostname.startsWith('cdn.')) {
			versionUrl = 'version.json';
		} else {
			// Default to /version.json
			versionUrl = '/version.json';
		}

		const response = await fetch(versionUrl);
		if (!response.ok) {
			logger.warn(`Failed to fetch version: ${response.status} ${response.statusText}`);
			return {
				env,
				name: 'unknown',
				version: 'unknown',
			};
		}

		const versionData = await response.json();

		return {
			env,
			name: versionData.name || 'unknown',
			version: versionData.version || 'unknown',
		};
	} catch (error) {
		logger.warn('Failed to fetch version information:', error as Error);
	}
	return {
		env,
		name,
		version,
	};
};

export class OpenObserve implements Destination {
	public name = OPENOBSERVE_DESTINATION_NAME;
	public priority = 10;
	// private analytics: Analytics;

	constructor(_analytics: Analytics) {
		logger.info('Initializing OpenObserve');
		// A failing RUM init must never break the other destinations.
		this.initialize().catch((error: unknown) => {
			logger.error('OpenObserve initialization failed:', error as Error);
		});
		// this.analytics = analytics;
	}

	private async initialize() {
		const info = await getServiceInfo();

		const options = {
			allowFallbackToLocalStorage: true,
			apiVersion: 'v1',
			applicationId: info.name,
			clientToken: 'rumdDWGm9mdLEqqcdPl',
			env: info.env,
			insecureHTTP: false,
			organizationIdentifier: 'default',
			service: info.name,
			site: siteFromUrl || buildUrl(URL_PATTERNS.OPENOBSERVE_API).replace('https://', ''),
			version: info.version,
		};

		openobserveRum.init({
			allowFallbackToLocalStorage: options.allowFallbackToLocalStorage,
			apiVersion: options.apiVersion,
			applicationId: options.applicationId,
			clientToken: options.clientToken,
			// User inputs are masked in session replay (RGPD): page content stays
			// readable, everything typed by the user is redacted.
			defaultPrivacyLevel: 'mask-user-input',
			env: options.env,
			insecureHTTP: options.insecureHTTP,
			organizationIdentifier: options.organizationIdentifier,
			service: options.service,
			sessionReplaySampleRate: SESSION_REPLAY_SAMPLE_RATE,
			sessionSampleRate: SESSION_SAMPLE_RATE,
			site: options.site,
			trackLongTasks: true,
			trackResources: true,
			trackUserInteractions: true,
			version: options.version,
		});

		openobserveLogs.init({
			allowFallbackToLocalStorage: options.allowFallbackToLocalStorage,
			apiVersion: options.apiVersion,
			clientToken: options.clientToken,
			env: options.env,
			// Sentry owns error capture (one client + app tag). Forwarding browser
			// errors here too would create a duplicate error pipeline.
			forwardErrorsToLogs: false,
			insecureHTTP: options.insecureHTTP,
			organizationIdentifier: options.organizationIdentifier,
			service: options.service,
			site: options.site,
			version: options.version,
		});

		// Stonal deployment dimensions (stack, provider domain) are not native RUM
		// fields, so they go into the global context under a `stonal` namespace.
		// They attach to every RUM event AND the session, so replays are queryable
		// by context.stonal.stack / context.stonal.providerDomain. Empty values
		// (e.g. localhost) are omitted rather than sent as empty strings.
		const stonalContext = {
			providerDomain: getProviderDomain() || undefined,
			stack: getStack() || undefined,
		};
		openobserveRum.setGlobalContextProperty('stonal', stonalContext);
		openobserveLogs.setGlobalContextProperty('stonal', stonalContext);

		logger.info('OpenObserve initialized');

		if (!isReplayDisabled()) {
			openobserveRum.startSessionReplayRecording();
		}
	}

	page(name: string, properties: any): void {
		try {
			// Add page view to RUM
			openobserveRum.setGlobalContextProperty('page', {
				name,
				...properties,
			});

			// Log page view
			openobserveLogs.logger.info('Page View', {
				page: name,
				url: window.location.href,
				...properties,
			});
		} catch (error) {
			logger.error('OpenObserve page tracking error:', error as Error);
		}
	}

	identify(userId: string | undefined, traits: any): void {
		if (!userId) {
			return;
		}

		logger.info('OpenObserve identify', {
			traits,
			userId,
		});

		try {
			// Set user context in RUM
			openobserveRum.setUser({
				id: userId,
				...traits,
			});

			// Add user context to logs
			openobserveLogs.setGlobalContextProperty('user', {
				id: userId,
				...traits,
			});

			// Log identification event
			openobserveLogs.logger.info('User Identified', {
				traits,
				userId,
			});
			if (sessionStorage.getItem(SESSION_KEY_OO_CURRENT_USER) !== userId) {
				if (!isReplayDisabled()) {
					logger.info('OpenObserve.identify: restarting session replay recording');
					openobserveRum.stopSessionReplayRecording();
					openobserveRum.startSessionReplayRecording();
				}
				sessionStorage.setItem(SESSION_KEY_OO_CURRENT_USER, userId);
			}
		} catch (error) {
			logger.error('OpenObserve identify error:', error as Error);
		}
	}

	track(event: string, properties: any): void {
		try {
			// Add custom action to RUM
			openobserveRum.addAction(event, properties);

			// Log the event
			openobserveLogs.logger.info('Event Tracked', {
				event,
				properties,
				timestamp: new Date().toISOString(),
			});
		} catch (error) {
			logger.error('OpenObserve track error:', error as Error);
		}
	}
}
